import { DBConnectionType } from '../../enums/db-connector/db-connection-type';
import { QueryType } from '../../enums/db-connector/query-type';
import { DBTaskConnectorType } from '../../types/db-task-connector-type';
import { DestinationTaskConfigBase } from '../config-objects/destination-service-config-base';
import { MySqlConnectionConfig } from './my-sql-connection-config';

/**
 * Configuration for a DatabaseConnector task — executes a SQL query against a MySQL
 * or MSSQL database. The query is resolved as an expression per input record.
 * SELECT results expand each row into a new record; write results return the driver result.
 */
export class DBConnectorConfig extends DestinationTaskConfigBase {
	/**
	 * Database engine type. MySQL=0, MSSQL=1.
	 * @default DBConnectionType.MySQL
	 */
	type: DBConnectionType = DBConnectionType.MySQL;

	/**
	 * Connection details for the target database. Use MySqlConnectionConfig for type=MySQL
	 * or MSSqlConnectionConfig for type=MSSQL or PostgreSQLConnectionConfig for type=PostgreSQL.
	 */
	connection: DBTaskConnectorType = new MySqlConnectionConfig();

	/**
	 * Default to false for backward compatibility. If true, the UI will allow the user to enter a plain SQL query instead of query builder UI.
	 * @default false
	 */
	usePlainStringQuery = false;

	/**
	 * Type of SQL query to execute. Will also determine how the UI is built.
	 * @default QueryType.SELECT
	 */
	queryType: QueryType = QueryType.SELECT;

	/**
	 * SQL query to execute. Supports expression syntax (e.g. '{{record.processingData.id}}').
	 * Resolved and executed once per input record.
	 * @default ''
	 */
	query = '';

	/**
	 * If true, the offset value will be treated as an expression and resolved per input record. If false, the offset value will be treated as a static number.
	 * @default false
	 */
	useExpressionForOffset = false;

	/**
	 * Offset the starting record for a SELECT query. For non-SELECT queries, this value is ignored.
	 * @default 0
	 */
	offset?: number | string = 0;

	/**
	 * Limit the number of records returned by a SELECT query. 0 is not allowed. A limit must always be set to avoid returning an unbounded number of records. For non-SELECT queries, this value is ignored.
	 * @default 10
	 */
	selectionLimit = 10;

	constructor(init?: Partial<DBConnectorConfig>) {
		super(init);
		Object.assign(this, init);
	}
}
